import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { BlogSeo } from "../components/BlogSeo";
import { FlameInitial } from "../components/FlameInitial";
import { CornerWebs } from "../components/CornerWebs";
import { FadeUp } from "../components/Reveal";

const SITE = "https://tattooedabroad.com";

const formatDate = (d) => {
  try {
    return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return d;
  }
};

export default function TattooTimes() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog/articles")
      .then((res) => setArticles(res.data.articles || []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <BlogSeo
        title="Tattoo Times | Tattooed Abroad"
        description="Stories from the chair and the road — tattoo artists, travel and collecting, with Jordy Trachtenberg."
        canonical={`${SITE}/tattoo-times`}
      />
      <section className="relative overflow-hidden bg-ink pt-28 pb-14 md:pt-32 md:pb-16">
        <div className="pointer-events-none absolute left-1/2 top-1/3 z-0 h-[520px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-flame opacity-[0.14] blur-[160px]" />
        <CornerWebs />
        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center">
          <p className="font-gothic text-xs uppercase tracking-[0.35em] text-flame md:text-sm">The Blog</p>
          <h1 data-testid="tattoo-times-heading" className="mt-5 font-display text-5xl font-light leading-none tracking-tight text-white md:text-7xl">
            <FlameInitial letter="T" size="0.9em" className="mr-[0.02em] translate-y-[0.06em]" />attoo{" "}
            <FlameInitial letter="T" size="0.9em" className="mr-[0.02em] translate-y-[0.06em]" />imes
          </h1>
          <p className="mx-auto mt-6 max-w-2xl font-body text-lg leading-relaxed text-white/65">
            Stories from the chair and the road. Tattoo artists, travel and collecting, with Jordy Trachtenberg.
          </p>
        </div>
      </section>

      <section className="relative bg-ink pb-24 md:pb-28">
        <div className="mx-auto max-w-4xl px-6">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin text-flame" size={28} /></div>
          ) : articles.length === 0 ? (
            <p data-testid="tattoo-times-empty" className="py-16 text-center font-body text-white/40">
              New stories are on the way. Check back soon.
            </p>
          ) : (
            <div className="divide-y divide-white/10 border-y border-white/10">
              {articles.map((a) => (
                <FadeUp key={a.slug}>
                  <article data-testid="article-card" className="grid gap-6 py-10 md:grid-cols-[200px_1fr]">
                    {a.photo ? (
                      <Link to={`/tattoo-times/${a.slug}`} className="block overflow-hidden">
                        <img src={a.photo} alt={a.title} className="aspect-[4/3] w-full border border-white/10 object-cover transition-transform duration-500 hover:scale-105" />
                      </Link>
                    ) : (
                      <div className="hidden md:block" />
                    )}
                    <div>
                      <p className="font-body text-xs uppercase tracking-[0.25em] text-white/40">{formatDate(a.date)}</p>
                      <h2 className="mt-3 font-display text-2xl font-normal leading-snug text-white md:text-3xl">
                        <Link to={`/tattoo-times/${a.slug}`} className="transition-colors hover:text-flame">{a.title}</Link>
                      </h2>
                      {a.excerpt && <p className="mt-3 font-body text-base leading-relaxed text-white/55">{a.excerpt}</p>}
                      <Link
                        to={`/tattoo-times/${a.slug}`}
                        data-testid={`read-article-${a.slug}`}
                        className="group mt-5 inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.25em] text-flame"
                      >
                        Read article
                        <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </article>
                </FadeUp>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
